/**
 * Pre-use forms per equipment type (api#1209) — the rules the office pages and
 * the phone check share: who may do what, how a refusal reads, and how the
 * server's derived result is put into words.
 *
 * ROLES, as the API gates them (preuse-templates.routes.ts):
 *   read            admin, office_staff, supervisor, dispatcher
 *   write (drafts)  requireRole('admin', 'office_staff') — non-strict, so a
 *                   dispatcher on a tenant with dispatcher_admin_access is let in
 *   publish/archive requireRole('admin', { strict: true }) — admin only, ever
 * The server is the authority; these only decide which controls to draw.
 */
import type { PreuseCheckResult, PreuseTemplateItem } from '../types/preuse';

export const PREUSE_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;

export function canReadPreuse(role: string | null | undefined): boolean {
  return !!role && (PREUSE_READ_ROLES as readonly string[]).includes(role);
}

/** Create and edit drafts, duplicate, delete a draft. */
export function canWritePreuse(role: string | null | undefined, dispatcherElevated = false): boolean {
  if (role === 'admin' || role === 'office_staff') return true;
  return role === 'dispatcher' && dispatcherElevated;
}

/** Publish and archive: strict admin. An elevated dispatcher is NOT let in. */
export function canPublishPreuse(role: string | null | undefined): boolean {
  return role === 'admin';
}

export const PREUSE_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  active: 'Live',
  archived: 'Archived',
};

/** Saturated fill, dark ink (the register badge idiom). Unknown → slate, reads `—`. */
export const PREUSE_STATUS_FILL: Record<string, string> = {
  draft: 'bg-fi-signal-amber',
  active: 'bg-fi-signal-green',
  archived: 'bg-fi-signal-slate',
};

export const PREUSE_STATUS_RAIL: Record<string, string> = {
  draft: 'border-l-fi-amber',
  active: 'border-l-fi-green',
  archived: 'border-l-fi-steel',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * A refused request, in a sentence a reader can act on. The API's `code` is
 * matched first; a 403 is a permission sentence whatever the body says; an
 * uncoded refusal falls through to the API's own message, then `fallback`.
 */
export function preuseRefusalSentence(error: any, fallback: string): string {
  const status: number | undefined = error?.response?.status;
  const body = error?.response?.data ?? {};
  const code: string | undefined = typeof body.code === 'string' ? body.code : undefined;
  const keys: string[] = Array.isArray(body.keys) ? body.keys : [];
  switch (code) {
    // ── template register ──
    case 'not_draft':
      return 'Only a draft can be changed. This template is no longer a draft — duplicate it to edit a copy.';
    case 'not_active':
      return 'Only a live template can be archived. A draft is deleted instead.';
    case 'publish_conflict':
      return 'Another template for this equipment type was published at the same moment. Reload the page and try again.';
    case 'duplicate_item_key':
      return 'Two items on this template have the same key. Remove the duplicate and save again.';
    // ── the phone check ──
    case 'template_not_found':
      return 'The pre-use form for this unit could not be found. Reload and try again.';
    case 'template_not_active':
      return 'The office replaced this unit’s pre-use form while you were filling it in. Reload to get the current form.';
    case 'template_type_mismatch':
      return 'This pre-use form is for a different equipment type than this unit. Reload and try again.';
    case 'unknown_item':
      return 'This pre-use form changed while you were filling it in. Reload to get the current form.';
    case 'missing_required_items':
      return `Answer every required item before saving — ${plural(keys.length || 1, 'item is', 'items are')} not answered.`;
    case 'duplicate_item':
    case 'invalid_result':
    case 'invalid_item':
    case 'checklist_items_required':
      return 'One of the answers could not be read. Check each item and save again.';
    case 'template_requires_use_check':
      return 'A pre-use form answers a pre-use or post-use check only.';
    case 'invalid_template_id':
      return 'The pre-use form for this unit could not be read. Reload and try again.';
    case 'status_conflict':
      return 'The result is worked out from the items. Reload and save again.';
    default:
      break;
  }
  if (status === 403) return 'You do not have permission to do that. Ask an admin.';
  if (status === 404) return 'That was not found. It may have been deleted — reload the page.';
  const text = typeof body.error === 'string' && body.error ? body.error : null;
  return text ?? fallback;
}

/** Items grouped by section, in sort order; a null section is grouped under "General". */
export function groupItemsBySection<T extends Pick<PreuseTemplateItem, 'section' | 'sortOrder'>>(items: T[]): Array<{ section: string; items: T[] }> {
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  const groups: Array<{ section: string; items: T[] }> = [];
  for (const it of sorted) {
    const name = it.section?.trim() || 'General';
    let g = groups.find((x) => x.section === name);
    if (!g) {
      g = { section: name, items: [] };
      groups.push(g);
    }
    g.items.push(it);
  }
  return groups;
}

export interface DerivedResultCopy {
  /** 'pass' | 'needs_repair' | 'fail' | 'unknown' — drives the tone only. */
  tone: 'pass' | 'needs_repair' | 'fail' | 'unknown';
  title: string;
  detail: string;
}

/**
 * The server's verdict on a templated check, from `derived_status` and its
 * counts only. Deliberately says NOTHING about what happens to the unit: an
 * API follow-up changes what a non-critical failure does (the unit may stay in
 * service with a maintenance item opened), so any consequence sentence belongs
 * to the field that follow-up adds, appended by the caller — not guessed here.
 */
/**
 * What the check did to the UNIT, read only from the fields api#1211 adds to
 * the `preuse` block. Each field is read on its own and an ABSENT field says
 * nothing — an API without #1211 yields no sentence, never a guess. "Stays in
 * service" is claimed only when the server said `unit_status_changed: false`.
 */
export function describeUnitConsequence(preuse: PreuseCheckResult | null | undefined): string[] {
  if (!preuse) return [];
  const changed = typeof preuse.unit_status_changed === 'boolean' ? preuse.unit_status_changed : null;
  const item = preuse.maintenance_item && typeof preuse.maintenance_item === 'object' ? preuse.maintenance_item : null;
  const out: string[] = [];
  if (changed === true) out.push('Unit taken out of service.');
  if (item) out.push(changed === false ? 'Maintenance item created — unit stays in service.' : 'Maintenance item created.');
  return out;
}

export function describeDerivedResult(preuse: PreuseCheckResult | null | undefined): DerivedResultCopy {
  const fails = typeof preuse?.fail_count === 'number' ? preuse.fail_count : null;
  const critical = typeof preuse?.critical_fail_count === 'number' ? preuse.critical_fail_count : null;
  switch (preuse?.derived_status) {
    case 'pass':
      return { tone: 'pass', title: 'Passed', detail: 'No item failed.' };
    case 'needs_repair':
      return {
        tone: 'needs_repair',
        title: 'Needs repair',
        detail: fails === null ? 'An item failed. None of the failed items is critical.' : `${plural(fails, 'item', 'items')} failed. None of them is critical.`,
      };
    case 'fail':
      return {
        tone: 'fail',
        title: 'Failed',
        detail:
          critical === null
            ? 'A critical item failed.'
            : `${plural(critical, 'critical item', 'critical items')} failed${fails !== null && fails !== critical ? ` (${plural(fails, 'item', 'items')} failed in all)` : ''}.`,
      };
    default:
      return { tone: 'unknown', title: 'Saved', detail: 'The check was saved. Its result could not be read.' };
  }
}
